import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TABS = ["Overview", "Products", "Rooms", "Scientists"];

export default function VRLab() {
  const [tab, setTab] = useState("Overview");
  return (
    <div data-testid="admin-vrlab">
      <h1 className="text-2xl font-semibold text-white">VR Lab</h1>
      <p className="text-slate-400 text-sm mt-1">Manage the immersive laboratory, place products, configure AI scientists and review analytics.</p>
      <div className="flex gap-2 mt-5 border-b border-white/10">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`vrlab-tab-${t}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400"}`}>{t}</button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "Overview" && <Overview />}
        {tab === "Products" && <Products />}
        {tab === "Rooms" && <Rooms />}
        {tab === "Scientists" && <Scientists />}
      </div>
    </div>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/vr/admin/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="text-slate-500 text-sm font-mono">Loading analytics…</div>;
  const f = d.funnel || {};
  const steps = [["Enter", f.enter], ["Inspect", f.inspect], ["Add to cart", f.add_to_cart], ["Checkout", f.checkout], ["Purchase", f.purchase]];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="VR Sessions" value={d.sessions} />
        <Stat label="Scientist Questions" value={d.conversations} />
        <Stat label="COA Scans" value={d.coa_scans} />
        <Stat label="Devices" value={Object.keys(d.by_device || {}).length} />
      </div>
      <div className="rounded-2xl border border-white/10 p-5">
        <div className="text-sm font-semibold text-white mb-3">VR Funnel</div>
        <div className="flex flex-wrap gap-2 items-end">
          {steps.map(([k, v], i) => (
            <div key={k} className="flex-1 min-w-[90px]">
              <div className="text-2xl font-semibold text-cyan-300">{v || 0}</div>
              <div className="text-[11px] text-slate-400 font-mono uppercase">{k}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <TopList title="Top Rooms" rows={d.top_rooms} />
        <TopList title="Most Discussed Peptides" rows={d.top_peptides} />
      </div>
    </div>
  );
}
const Stat = ({ label, value }) => (<div className="rounded-2xl border border-white/10 p-4"><div className="text-2xl font-semibold text-white">{value ?? 0}</div><div className="text-[11px] text-slate-400 font-mono uppercase mt-1">{label}</div></div>);
const TopList = ({ title, rows }) => (
  <div className="rounded-2xl border border-white/10 p-5">
    <div className="text-sm font-semibold text-white mb-2">{title}</div>
    {(rows || []).length === 0 ? <div className="text-xs text-slate-500">No data yet</div> :
      (rows || []).map(([k, v]) => (<div key={k} className="flex justify-between text-sm text-slate-300 py-1"><span>{k}</span><span className="font-mono text-cyan-300">{v}</span></div>))}
  </div>
);

function Products() {
  const [catalog, setCatalog] = useState([]);
  const [rooms, setRooms] = useState([]);
  const load = () => {
    api.get("/vr/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
    api.get("/vr/admin/rooms").then(({ data }) => setRooms(data.filter((r) => ["gallery", "performance", "longevity", "metabolic", "cognition"].includes(r.key)))).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const save = async (p, patch) => {
    const body = { product_slug: p.slug, room: patch.room ?? p.vr.room ?? "gallery", featured: patch.featured ?? p.vr.featured, sort_order: patch.sort_order ?? p.vr.sort_order, enabled: true };
    await api.post("/vr/admin/placement", body);
    toast.success(`${p.name} placed`); load();
  };
  return (
    <div className="rounded-2xl border border-white/10 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.03] text-slate-400 text-xs"><tr><th className="text-left p-3">Product</th><th className="text-left p-3">Room</th><th className="text-left p-3">Featured</th></tr></thead>
        <tbody>
          {catalog.map((p) => (
            <tr key={p.slug} className="border-t border-white/5" data-testid={`vrlab-prod-${p.slug}`}>
              <td className="p-3 text-slate-200">{p.name}</td>
              <td className="p-3">
                <select defaultValue={p.vr.room || "gallery"} onChange={(e) => save(p, { room: e.target.value })}
                  className="bg-black/40 border border-white/15 rounded-lg px-2 py-1 text-slate-200 text-xs">
                  <option value="gallery">gallery</option>
                  {rooms.filter((r) => r.key !== "gallery").map((r) => <option key={r.key} value={r.key}>{r.key}</option>)}
                </select>
              </td>
              <td className="p-3"><input type="checkbox" defaultChecked={p.vr.featured} onChange={(e) => save(p, { featured: e.target.checked })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Rooms() {
  const [rooms, setRooms] = useState([]);
  const load = () => api.get("/vr/admin/rooms").then(({ data }) => setRooms(data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  const toggle = async (r) => { await api.put(`/vr/admin/rooms/${r.key}`, { enabled: !r.enabled }); toast.success(`${r.name} ${!r.enabled ? "enabled" : "disabled"}`); load(); };
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {rooms.map((r) => (
        <div key={r.key} className="rounded-2xl border border-white/10 p-4 flex items-center justify-between" data-testid={`vrlab-room-${r.key}`}>
          <div><div className="text-sm font-semibold text-white">{r.name}</div><div className="text-[11px] text-slate-400">{r.description || r.type}</div></div>
          <button onClick={() => toggle(r)} className={`text-xs font-mono px-3 py-1.5 rounded-lg border ${r.enabled ? "border-emerald-400 text-emerald-300" : "border-white/15 text-slate-400"}`}>{r.enabled ? "ENABLED" : "DISABLED"}</button>
        </div>
      ))}
    </div>
  );
}

function Scientists() {
  const [list, setList] = useState([]);
  const load = () => api.get("/vr/admin/scientists").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  const save = async (s, patch) => { await api.post("/vr/admin/scientist", { ...s, ...patch }); toast.success("Scientist saved"); load(); };
  return (
    <div className="space-y-3">
      {list.map((s) => (
        <div key={s.key} className="rounded-2xl border border-white/10 p-4" data-testid={`vrlab-sci-${s.key}`}>
          <div className="flex items-center gap-3">
            <input defaultValue={s.name} onBlur={(e) => save(s, { name: e.target.value })} className="bg-black/40 border border-white/15 rounded-lg px-3 py-1.5 text-white text-sm font-semibold" />
            <input defaultValue={s.title} onBlur={(e) => save(s, { title: e.target.value })} className="flex-1 bg-black/40 border border-white/15 rounded-lg px-3 py-1.5 text-slate-200 text-sm" />
            <button onClick={() => save(s, { active: !s.active })} className={`text-xs font-mono px-3 py-1.5 rounded-lg border ${s.active ? "border-emerald-400 text-emerald-300" : "border-white/15 text-slate-400"}`}>{s.active ? "ACTIVE" : "OFF"}</button>
          </div>
          <textarea defaultValue={s.specialty} onBlur={(e) => save(s, { specialty: e.target.value })} className="mt-2 w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-slate-300 text-xs" rows={2} />
        </div>
      ))}
    </div>
  );
}
